import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { MDBBox } from "mdbreact";
import jwt_decode from "jwt-decode";

import Loading from "../ui-components/loading";

import { authorize } from "api/auth/authorization";
import { followStagingRedirect } from "helpers/emailLinks";
import { setJWT, tokenRef, suspendedFlagRef } from "api";
import addNotification from "helpers/notify";
import { useDispatch } from "react-redux";
import { useHttp } from "hooks/useHttp";
import { setUser } from "actions/user";
import capitalize from "helpers/capitalize";
import GA4 from "../../components/GA4";
import { eventTrack } from "../../helpers/ga4Helper";
import { url } from "api";
import CompanyPicker from "components/CompanyPicker/CompanyPicker";
import { getMyCompanies } from "api/companyMembership/companyMembership";

const Login = (props) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stayedInSystem, setStayedInSystem] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const [companyChoices, setCompanyChoices] = useState(null);

  // Landed here because the session was cut off by a company suspension
  // (api/index.js suspendedSessionGate). Say so once, then forget it.
  useEffect(() => {
    try {
      const flag = sessionStorage.getItem(suspendedFlagRef);
      if (flag === "1") {
        sessionStorage.removeItem(suspendedFlagRef);
        addNotification(
          "Your account is suspended, please contact support@bidlight.com",
          "danger"
        );
      } else if (flag === "paused") {
        // One person's login paused by their company (back/utils/loginSuspension).
        sessionStorage.removeItem(suspendedFlagRef);
        addNotification(
          "Your account has been paused by your company administrator. Contact them to get access back.",
          "danger"
        );
      }
    } catch (e) { /* storage unavailable */ }
  }, []);

  // Partner staff added by name and email have no password: their way in
  // is a link by email (back/utils/signInLink). A failed password attempt
  // on such an account already sends one (the server says so), and this
  // button asks for one directly.
  const [linkBusy, setLinkBusy] = useState(false);
  const emailSignInLink = async () => {
    const address = String(email || "").trim();
    if (!address) {
      addNotification("Type the email address you were invited with, then press the link again.", "danger");
      return;
    }
    setLinkBusy(true);
    try {
      const r = await fetch(`${url}/users/email-sign-in-link`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: address, next: props.fromPath }),
      });
      const b = await r.json().catch(() => ({}));
      if (followStagingRedirect(b)) return;
      addNotification(b.message || b.err || "Could not send the link", r.ok ? "success" : "danger");
    } catch (e) {
      addNotification("Could not send the link - please try again", "danger");
    } finally {
      setLinkBusy(false);
    }
  };

  const { loading, request: authorizeAPI } = useHttp({
    requestCallback: (data) => authorize(data),
    onLoad: (res) => {
      if (followStagingRedirect(res)) return;
      if (res.err) {
        addNotification(res.err, res.signInLinkSent ? "success" : "danger");
      } else {
        localStorage.setItem(tokenRef, res.token);
        setJWT(res.token);
        const token = jwt_decode(res.token);
        dispatch(setUser(token));
        // More than one company: ask which one to work in first. Any
        // failure here signs the person in exactly as before.
        getMyCompanies()
          .then((r) => {
            const rows = (r && r.memberships) || [];
            if (rows.length >= 2) setCompanyChoices(rows);
            else history.push(props.fromPath);
          })
          .catch(() => history.push(props.fromPath));
      }
    },
    onError: (err) => {
      if (followStagingRedirect(err)) return;
      addNotification(err.err, err && err.signInLinkSent ? "success" : "danger");
    },
  });

  const submitAuthorization = (email, password) => (e) => {
    eventTrack(
      "Login-Action",
      "Login",
      history.location.pathname,
      email,
      false,
      "xhr"
    );
    e.preventDefault();
    if (email.length === 0) {
      addNotification("Email field must not be empty!", "danger");
    } else if (password.length === 0) {
      addNotification("Password field must not be empty!", "danger");
    } else {
      authorizeAPI({ email, password, keep: stayedInSystem, next: props.fromPath });
    }
  };

  return (
    <form
      className="mt-1 px-4 box-login"
      onSubmit={submitAuthorization(email, password)}
    >
      <h2 className="text-center title-login">Login</h2>
      <div className="form-group">
        <div className="icon_login">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            id="mail"
            width="18"
            height="14.324"
            viewBox="0 0 18 14.324"
          >
            <path
              id="mail-2"
              data-name="mail"
              d="M3.01,5.838a1,1,0,0,1,1-1H20a1,1,0,0,1,1,1V17.162a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2v-11a1.008,1.008,0,0,1,.01-.14ZM5,8.062v9.1H19v-9.1l-4.878,4.879a3,3,0,0,1-4.243,0ZM6.572,6.806H17.428l-4.721,4.721a1,1,0,0,1-1.414,0Z"
              transform="translate(-3 -4.838)"
              fill="#aeaeae"
            />
          </svg>
        </div>
        <input
          type="email"
          className="input-login"
          placeholder="Your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="position-relative">
        <div className="form-group">
          <div className="icon_login">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              id="lock"
              width="18"
              height="21"
              viewBox="0 0 18 21"
            >
              <path
                id="lock-2"
                data-name="lock"
                d="M18,10.5a3,3,0,0,1,3,3v6a3,3,0,0,1-3,3H6a3,3,0,0,1-3-3v-6a3,3,0,0,1,3-3v-3a6,6,0,0,1,12,0Zm-6-7a4,4,0,0,1,4,4v3H8v-3A4,4,0,0,1,12,3.5Zm6,9H6a1,1,0,0,0-1,1v6a1,1,0,0,0,1,1H18a1,1,0,0,0,1-1v-6A1,1,0,0,0,18,12.5Z"
                transform="translate(-3 -1.5)"
                fill="#aeaeae"
              />
            </svg>
          </div>
          <input
            type={showPassword ? "text" : "password"}
            className="input-login"
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <i
            className={`password-eye fa ${
              showPassword ? "fa-eye-slash" : "fa-eye"
            }`}
            onClick={() =>
              setShowPassword((prevShowPassword) => !prevShowPassword)
            }
          ></i>
        </div>
      </div>
      <div>
        <MDBBox className="d-flex justify-space-between mt-3">
          <div className="custom-control custom-checkbox">
            <input
              type="checkbox"
              className="custom-control-input cursor-pointer"
              id="stay-in-system"
              onChange={() => setStayedInSystem((s) => !s)}
            />
            <label
              className="custom-control-label cursor-pointer"
              htmlFor="stay-in-system"
            >
              Keep me signed in
            </label>
          </div>
        </MDBBox>
      </div>
      {loading ? <Loading color="black" text="Authorizing..." /> : null}

      <div className="form-group">
        <input
          type="submit"
          className="btn-login-login"
          value="Login"
          disabled={loading}
        />
      </div>
      <div className="text-center" style={{ marginTop: -4, marginBottom: 8, fontSize: 13 }}>
        <button
          type="button"
          onClick={emailSignInLink}
          disabled={linkBusy}
          style={{ background: "none", border: 0, padding: 0, color: "#2F6F9F", textDecoration: "underline", cursor: "pointer" }}
        >
          {linkBusy ? "Sending..." : "No password? Email me a sign-in link"}
        </button>
        <div style={{ color: "#8a8a8a", fontSize: 12, marginTop: 2 }}>
          For partners and contractors added by name and email
        </div>
      </div>
      <GA4
        userId={email}
        category={"Navigation"}
        action={"Login-Action"}
        label={history.location.pathname}
        value={email}
        nonInteraction={false}
        transport={"xhr"}
      ></GA4>
      <CompanyPicker
        open={!!companyChoices}
        memberships={companyChoices || []}
        onStay={() => {
          setCompanyChoices(null);
          history.push(props.fromPath);
        }}
      />
    </form>
  );
};

export default Login;
